/**
 * Bobo's loading state for list screens: the thinking mascot with a speech bubble whose cute quip rotates every
 * few seconds (random start, never the same line twice in a row). Replaces a bare full-screen spinner on the
 * initial load. With reduced motion only the first line is shown and nothing rotates.
 *
 * @module components/bobo/BoboLoading
 */

import React, {useEffect, useMemo, useState} from 'react';
import {AccessibilityInfo, StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import BoboMascot from './BoboMascot';

export const ROTATE_MS = 2800;

/**
 * @param {number} count Number of messages.
 * @param {number} [previous] Index to avoid.
 * @returns {number} A random index different from `previous` when there is more than one message.
 */
export const nextIndex = (count, previous = -1) => {
    if (count <= 1) {
        return 0;
    }
    let next = Math.floor(Math.random() * count);

    if (next === previous) {
        next = (next + 1) % count;
    }

    return next;
};

/**
 * @param {Object} props Props.
 * @param {number} [props.size] Width of Bobo in dp.
 * @param {boolean} [props.compact] Tighter padding, for use inside a card or a short area.
 * @param {*} [props.style] Extra style of the wrapper.
 * @param {string[]} [props.messages] Override the localized quips (`boboLoading_messages`).
 * @returns {JSX.Element} The loading indicator.
 */
const BoboLoading = ({size = 96, compact = false, style, messages}) => {
    const {t} = useTranslation();
    const list = useMemo(() => {
        const source = Array.isArray(messages) && messages.length ? messages : t('boboLoading_messages', {returnObjects: true});

        return Array.isArray(source) ? source.filter((m) => typeof m === 'string' && m) : [];
    }, [messages, t]);
    const [reduced, setReduced] = useState(false);
    const [index, setIndex] = useState(() => nextIndex(list.length));

    useEffect(() => {
        let cancelled = false;

        AccessibilityInfo.isReduceMotionEnabled?.().then((value) => {
            if (!cancelled) {
                setReduced(!!value);
            }
        }).catch(() => {});

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (reduced || list.length < 2) {
            return undefined;
        }
        const timer = globalThis.setInterval(() => setIndex((prev) => nextIndex(list.length, prev)), ROTATE_MS);

        return () => globalThis.clearInterval(timer);
    }, [reduced, list]);

    const message = list.length ? list[reduced ? 0 : Math.min(index, list.length - 1)] : null;

    return (
        <View
            accessible
            accessibilityRole="progressbar"
            accessibilityLabel={message || t('data_loading')}
            accessibilityState={{busy: true}}
            style={[styles.wrap, compact && styles.compact, style]}
        >
            <BoboMascot size={size} mood="thinking" layout="stack" interactive message={message} />
        </View>
    );
};

const styles = StyleSheet.create({
    wrap: {flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 32, paddingVertical: 32},
    compact: {flex: 0, paddingVertical: 16},
});

export default BoboLoading;
